import { createContext, JSX, ReactNode, useContext, useEffect, useState } from 'react';

export type Indicator = 'fullScreen' | 'local' | 'custom';
export type Status = 'alert' | 'success' | 'error';
type DialogueProps = {
  isVisible: boolean;
  title: string;
  subTitle: string;
  status: Status;
  confirm: { label: string; action: () => void };
  cancel?: { label: string; action: () => void };
  showDialogue: () => void;
  provideProps: (props: ConfirmationDialogueProps) => void;
};

export type ConfirmationDialogueProps = {
  title: string;
  subTitle: string;
  status: Status;
  confirm: { label: string; action: () => void };
  cancel?: { label: string; action: () => void };
};

const defaultProps: ConfirmationDialogueProps & { isVisible: boolean } = {
  isVisible: false,
  title: '',
  subTitle: '',
  status: 'success',
  confirm: { label: 'Confirm', action: () => {} },
};

export const ConfirmationDialogueContext = createContext<DialogueProps>({
  isVisible: false,
  title: '',
  subTitle: '',
  status: 'success',
  confirm: { label: '', action: () => {} },
  showDialogue: () => {},
  provideProps: () => {},
});

export const ConfirmationDialogueProvider = ({ children }: { children: ReactNode }): JSX.Element => {
  const [props, setProps] = useState(defaultProps);

  const [isVisible, setIsVisible] = useState(false);
  const [title, setTitle] = useState(defaultProps.title);
  const [status, setStatus] = useState(defaultProps.status);
  const [subTitle, setSubTitle] = useState(defaultProps.subTitle);
  const [confirmLabel, setConfirmLabel] = useState(defaultProps.confirm.label);
  const [confirmAction, setConfirmAction] = useState(() => defaultProps.confirm.action);
  const [cancelLabel, setCancelLabel] = useState<string>();
  const [cancelAction, setCancelAction] = useState<() => void>();

  const provideProps = (_props: ConfirmationDialogueProps) => {
    setSubTitle(_props.subTitle);
    setTitle(_props.title);
    setStatus(_props.status);
    setConfirmLabel(_props.confirm.label);
    setConfirmAction(() => _props.confirm.action);
    setCancelLabel(_props.cancel?.label);
    setCancelAction(() => _props.cancel?.action);
  };

  const showDialogue = () => {
    setIsVisible(true);
  };

  useEffect(() => {
    const requiredProps = {
      isVisible,
      title,
      subTitle,
      status,
      confirm: {
        label: confirmLabel,
        action: () => {
          confirmAction();
          setIsVisible(false);
        },
      },
    };

    const cancel = {
      label: cancelLabel || '',
      action: () => {
        if (cancelAction) cancelAction();
        setIsVisible(false);
      },
    };

    setProps(cancelLabel && cancelAction ? { ...requiredProps, cancel } : requiredProps);
  }, [isVisible, title, confirmLabel, cancelLabel]);

  return (
    <ConfirmationDialogueContext.Provider value={{ ...props, provideProps, showDialogue }}>
      {children}
    </ConfirmationDialogueContext.Provider>
  );
};

export const useConfirmationDialogue = (): DialogueProps => useContext(ConfirmationDialogueContext);
